import type { ComparisonPage } from '../types';

export const klarVsWarehouse: ComparisonPage = {
  slug: 'klar-vs-custom-data-warehouse',
  group: 'ecomm',
  ours: 'greyfield',
  theirs: 'klar',
  title: 'Klar vs a custom data warehouse: contribution margin out of the box or your own model?',
  metaTitle: 'Klar vs a Custom Data Warehouse (2026): Honest Comparison',
  description:
    'Klar gives European DTC brands contribution margin, retention, and attribution from €200 a month. Here is when that fits and when you need your own warehouse.',
  keywords: ['klar alternative', 'klar vs', 'klar pricing', 'klar contribution margin', 'ecommerce profit analytics europe'],
  published: '2026-11-03',
  updated: '2026-11-03',
  tldr:
    'Klar is a strong fit for European Shopify and Shopware brands that want a contribution-margin P&L, retention cohorts, and attribution with EU data hosting, set up in days. Klar itself says it has no database you can query and no custom dashboard builder yet, so brands that need SQL, custom reports, or their data joined to other systems need a warehouse.',
  differences: [
    { heading: 'Contribution margin, ready-made', body: 'Klar ships a margin waterfall with product, logistics, and payment costs on day one. A build models the same thing to your rules.' },
    { heading: 'No queryable database', body: 'Klar says it does not run a database you can query; data leaves through its public API. A build lives in Snowflake you query directly.' },
    { heading: 'Measurement included', body: 'Klar’s higher plan adds a pixel, attribution, MMM, and geo-lift tests. We do not build those.' },
  ],
  sections: [
    {
      heading: 'What Klar does well',
      paragraphs: [
        'Its contribution-margin and retention views are built for how DTC operators think, and its measurement engine combines attribution, MMM, and geo-lift tests. Pricing is based on trailing twelve-month net revenue, from €200 a month, with free onboarding, no setup fee, EU hosting, and ISO 27001 certification.',
      ],
    },
    {
      heading: 'Where it stops',
      paragraphs: [
        'By Klar’s own account, there is no custom dashboard builder yet, and its AI lags the category leaders. Its table view picks from predefined metrics. TikTok Shop orders arrive through the Shopify sync, ERPs push orders through its API, and Amazon syncs daily with 30 days of history, with costs added manually.',
      ],
    },
  ],
  pickThem: [
    'You are a European DTC brand that wants margin, retention, and attribution in one tool.',
    'EU hosting and ISO 27001 matter to you.',
    'You want to be running within days for a few hundred euros a month.',
  ],
  pickUs: [
    'You need to query your data with SQL or join it to ERP and finance data.',
    'You need custom dashboards for specific teams.',
    'Amazon, wholesale, or several brands need full history in one model.',
  ],
  byTeam: [
    { team: 'EU Shopify or Shopware DTC brand', pick: 'theirs', why: 'Margin, retention, and attribution with EU hosting.' },
    { team: 'Brand with a finance team on an ERP', pick: 'ours', why: 'Orders and costs joined to finance data in your warehouse.' },
    { team: 'Brand needing custom reports per team', pick: 'ours', why: 'Sigma workbooks built for each team on your models.' },
  ],
  faq: [
    {
      question: 'Does a custom warehouse replace Klar?',
      answer: 'For reporting, yes, if you need SQL access or custom dashboards. For attribution and MMM, no: we do not build those, so brands that rely on Klar’s measurement may keep it.',
    },
    {
      question: 'How much does Klar cost?',
      answer: 'From €200 a month for Core and €400 a month with attribution, based on trailing twelve-month net revenue, with free onboarding and no long-term contract.',
    },
  ],
  related: ['lifetimely-vs-custom-data-warehouse', 'northbeam-vs-custom-data-warehouse', 'polar-analytics-vs-custom-data-warehouse'],
};
